'use client';

import { PlayerType, GameSettingsType } from '../types';
import { useState } from 'react';

type GameOverModalProps = {
  isVisible: boolean;
  winner: PlayerType | null;
  moveCount: number;
  onNewGame: () => void;
  settings: GameSettingsType;
};

export const GameOverModal: React.FC<GameOverModalProps> = ({
  isVisible,
  winner,
  moveCount,
  onNewGame,
  settings,
}) => {
  if (!isVisible) return null;
  
  // State to track modal visibility
  const [visible, setVisible] = useState(true);

  // Get player label based on game mode
  const getPlayerLabel = (player: PlayerType) => {
    if (settings.gameMode === 'human-vs-human') {
      return player === 'BLACK' ? 'Black' : 'White';
    } else {
      // Human vs AI mode
      const isHuman = player === settings.humanPlayerColor;
      return isHuman 
        ? `You (${player === 'BLACK' ? 'Black' : 'White'})` 
        : `AI (${player === 'BLACK' ? 'Black' : 'White'})`;
    }
  };

  // Determine result message for AI games
  const getResultMessage = () => {
    if (!winner) return "It's a draw!";
    
    if (settings.gameMode === 'human-vs-ai') {
      const isHumanWinner = winner === settings.humanPlayerColor;
      if (isHumanWinner) {
        return "You won!";
      } else {
        return "AI won!";
      }
    } else {
      return `${getPlayerLabel(winner)} wins!`;
    }
  };

  // Close modal when clicking outside of it
  const handleBackdropClick = (e: React.MouseEvent) => {
    // Only close if clicking the backdrop (not the modal itself)
    if (e.target === e.currentTarget) {
      // Hide modal but don't start new game
      // This allows player to view the board after game ends
      setVisible(false);
    }
  };

  if (!visible) return null;

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center z-50 bg-black/50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
        <h2 className="text-2xl font-bold text-center mb-4">Game Over</h2>
        
        {winner ? (
          <div className="text-center mb-6">
            <div className="flex items-center justify-center mb-4">
              <div className={`w-12 h-12 rounded-full ${
                winner === 'BLACK' ? 'bg-gray-900' : 'bg-gray-100 border border-gray-300'
              }`}></div>
            </div>
            <p className="text-xl">
              {getResultMessage()}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Game finished in {moveCount} moves
            </p>
            
            {settings.gameMode === 'human-vs-ai' && winner !== settings.humanPlayerColor && (
              <div className="mt-3 px-4 py-2 bg-yellow-50 rounded-md text-yellow-700 text-sm">
                {settings.aiDifficulty === 'easy' 
                  ? "Don't worry! The AI was just lucky. Try again!" 
                  : settings.aiDifficulty === 'medium'
                    ? "Good effort! Try adjusting your strategy next time."
                    : "That was a tough match! The AI is playing at its highest level."}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center mb-6">
            <p className="text-xl mb-2">It's a draw!</p>
            <p className="text-sm text-gray-500">
              The board is full after {moveCount} moves
            </p>
          </div>
        )}
        
        <div className="flex justify-between">
          <button
            onClick={() => setVisible(false)}
            className="btn btn-outline border-gray-300 hover:bg-gray-100 text-gray-700"
          >
            Close
          </button>
          <button
            onClick={onNewGame}
            className="btn bg-indigo-500 hover:bg-indigo-600 text-white border-none"
          >
            Play Again
          </button>
        </div>
      </div>
    </div>
  );
};

export default GameOverModal;